"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Save, Trash2, ChevronLeft, Plus, GripVertical, X, ArrowUp, ArrowDown, Settings, Eye } from "lucide-react";
import { renderTemplate } from "@/lib/template-vars";
import type { SampleLead } from "./template-editor";

const CHANNEL_LABEL: Record<string, string> = {
  EMAIL: "Email",
  WHATSAPP: "WhatsApp",
  SMS: "SMS",
  VOICE_AI: "Voz IA",
};
const CHANNEL_BG: Record<string, string> = {
  EMAIL: "#DBEAFE",
  WHATSAPP: "#D1FAE5",
  SMS: "#E9D5FF",
  VOICE_AI: "#FCE7F3",
};
const CHANNEL_FG: Record<string, string> = {
  EMAIL: "#1E40AF",
  WHATSAPP: "#065F46",
  SMS: "#6B21A8",
  VOICE_AI: "#9F0668",
};
const CHANNEL_EMOJI: Record<string, string> = {
  EMAIL: "✉",
  WHATSAPP: "💬",
  SMS: "📱",
  VOICE_AI: "📞",
};

type Template = {
  id: string;
  name: string;
  channel: string;
  subject: string | null;
  body: string;
};

type Step = {
  id?: string;
  ordering: number;
  dayOffset: number;
  hour: number;
  channel: "EMAIL" | "WHATSAPP" | "SMS" | "VOICE_AI";
  messageTemplateId: string | null;
  onlyHot: boolean;
  skipIfReplied: boolean;
};

export type CadenceData = {
  id?: string;
  name: string;
  active: boolean;
  steps: Step[];
};

export function CadenceEditor({
  initial,
  templates,
  sample,
  isNew = false,
}: {
  initial: CadenceData;
  templates: Template[];
  sample: SampleLead;
  isNew?: boolean;
}) {
  const router = useRouter();
  const [name, setName] = useState(initial.name);
  const [active, setActive] = useState(initial.active);
  const [steps, setSteps] = useState<Step[]>(initial.steps);
  const [selectedStepIdx, setSelectedStepIdx] = useState<number | null>(steps.length > 0 ? 0 : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tplById = Object.fromEntries(templates.map((t) => [t.id, t]));
  const selectedStep = selectedStepIdx != null ? steps[selectedStepIdx] : null;
  const selectedTpl = selectedStep?.messageTemplateId ? tplById[selectedStep.messageTemplateId] : null;

  function addStep() {
    const lastDay = steps.length > 0 ? steps[steps.length - 1].dayOffset : -1;
    const newStep: Step = {
      ordering: steps.length,
      dayOffset: lastDay + 1,
      hour: 10,
      channel: "EMAIL",
      messageTemplateId: null,
      onlyHot: false,
      skipIfReplied: true,
    };
    setSteps([...steps, newStep]);
    setSelectedStepIdx(steps.length);
  }

  function updateStep(idx: number, patch: Partial<Step>) {
    setSteps((s) => s.map((step, i) => (i === idx ? { ...step, ...patch } : step)));
  }

  function removeStep(idx: number) {
    if (!confirm("¿Eliminar este paso?")) return;
    setSteps((s) => s.filter((_, i) => i !== idx).map((step, i) => ({ ...step, ordering: i })));
    setSelectedStepIdx(null);
  }

  function moveStep(idx: number, dir: -1 | 1) {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= steps.length) return;
    const arr = [...steps];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setSteps(arr.map((step, i) => ({ ...step, ordering: i })));
    setSelectedStepIdx(newIdx);
  }

  async function handleSave() {
    setError(null);
    setSaving(true);
    const url = initial.id ? `/api/cadencias/${initial.id}` : "/api/cadencias";
    const res = await fetch(url, {
      method: initial.id ? "PATCH" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: name.trim(),
        active,
        steps: steps.map((s, i) => ({
          ordering: i,
          dayOffset: s.dayOffset,
          hour: s.hour,
          channel: s.channel,
          messageTemplateId: s.messageTemplateId,
          onlyHot: s.onlyHot,
          skipIfReplied: s.skipIfReplied,
        })),
      }),
    });
    setSaving(false);
    if (!res.ok) {
      setError(await res.text());
      return;
    }
    const json = await res.json();
    if (isNew) router.push(`/cadencias/${json.id}`);
    else router.refresh();
  }

  async function handleDelete() {
    if (!initial.id) return;
    if (!confirm(`¿Eliminar la cadencia "${name}"?`)) return;
    setSaving(true);
    const res = await fetch(`/api/cadencias/${initial.id}`, { method: "DELETE" });
    setSaving(false);
    if (!res.ok) {
      setError(await res.text());
      return;
    }
    router.push("/cadencias");
  }

  const templatesByChannel = (channel: string) => templates.filter((t) => t.channel === channel);

  return (
    <div className="p-8 max-w-[1500px]">
      <Link
        href="/cadencias"
        className="inline-flex items-center gap-1.5 text-[#6B7280] hover:text-[#CD0985] font-display text-[11.5px] font-semibold uppercase tracking-wide mb-4"
      >
        <ChevronLeft size={14} strokeWidth={2.5} />
        Volver a Cadencias
      </Link>

      <div className="flex items-end justify-between mb-6">
        <div className="flex-1">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre de la cadencia"
            className="font-sans text-3xl font-black tracking-tight text-[#111] bg-transparent border-0 outline-none focus:border-b-2 focus:border-[#CD0985] w-full max-w-md"
          />
          <p className="text-[#6B7280] mt-1 text-[13px] font-display">
            {steps.length} paso{steps.length === 1 ? "" : "s"} ·{" "}
            {steps.length > 0 ? `${Math.max(...steps.map((s) => s.dayOffset))} días de duración` : "sin pasos"}
          </p>
        </div>
        <div className="flex gap-2.5 items-center">
          <button
            onClick={() => setActive(!active)}
            className={`px-3.5 py-2 rounded-[8px] font-display font-bold text-[12px] tracking-wide ${
              active ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#F3F4F6] text-[#6B7280]"
            }`}
          >
            {active ? "✓ Activa" : "○ Inactiva"}
          </button>
          {!isNew && initial.id && (
            <button
              onClick={handleDelete}
              disabled={saving}
              className="px-4 py-2.5 bg-white border-[1.5px] border-[#FCA5A5] text-[#991B1B] rounded-[10px] font-display font-bold text-[12.5px] hover:bg-[#FEE2E2] inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Trash2 size={14} strokeWidth={2.5} />
              Eliminar
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-5 py-2.5 bg-[#CD0985] hover:bg-[#9F0668] disabled:opacity-50 text-white rounded-[10px] font-display font-bold text-[13px] inline-flex items-center gap-2 shadow-[0_4px_14px_rgba(205,9,133,0.35)]"
          >
            <Save size={15} strokeWidth={2.5} />
            {saving ? "Guardando..." : isNew ? "Crear cadencia" : "Guardar"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-[10px] text-sm text-red-800 font-medium">
          {error}
        </div>
      )}

      <div className="grid grid-cols-[1fr_440px] gap-6">
        {/* LEFT: STEPS FLOW */}
        <div className="bg-[#FAFAFB] border border-[#E5E7EB] rounded-[14px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
          style={{ backgroundImage: "radial-gradient(rgba(0,0,0,.04) 1px,transparent 1px)", backgroundSize: "22px 22px" }}
        >
          <div className="flex items-center justify-between mb-5">
            <div className="font-display text-[10.5px] tracking-[0.16em] uppercase text-[#6B7280] font-bold">
              Flujo de mensajes
            </div>
            <button
              onClick={addStep}
              className="px-3 py-1.5 bg-white border-[1.5px] border-[#E5E7EB] hover:border-[#CD0985] hover:text-[#CD0985] text-[#111] rounded-[8px] font-display font-bold text-[11.5px] inline-flex items-center gap-1.5"
            >
              <Plus size={13} strokeWidth={2.5} />
              Agregar paso
            </button>
          </div>

          {steps.length === 0 ? (
            <div className="text-center py-12 text-[#6B7280] font-display text-sm">
              Sin pasos todavía. Agregá el primero.
            </div>
          ) : (
            <div className="space-y-2">
              {steps.map((step, idx) => {
                const tpl = step.messageTemplateId ? tplById[step.messageTemplateId] : null;
                const selected = selectedStepIdx === idx;
                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedStepIdx(idx)}
                    className={`bg-white border-[1.5px] rounded-[12px] p-4 cursor-pointer transition-all ${
                      selected ? "border-[#CD0985] shadow-[0_4px_14px_rgba(205,9,133,0.15)]" : "border-[#E5E7EB] hover:border-[#9CA3AF]"
                    }`}
                    style={{ borderLeftWidth: 3, borderLeftColor: CHANNEL_FG[step.channel] }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col items-center gap-0.5 pt-0.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); moveStep(idx, -1); }}
                          disabled={idx === 0}
                          className="text-[#9CA3AF] hover:text-[#CD0985] disabled:opacity-30"
                        >
                          <ArrowUp size={12} strokeWidth={2.5} />
                        </button>
                        <GripVertical size={14} className="text-[#D1D5DB]" />
                        <button
                          onClick={(e) => { e.stopPropagation(); moveStep(idx, 1); }}
                          disabled={idx === steps.length - 1}
                          className="text-[#9CA3AF] hover:text-[#CD0985] disabled:opacity-30"
                        >
                          <ArrowDown size={12} strokeWidth={2.5} />
                        </button>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span
                            className="font-display text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                            style={{ background: CHANNEL_BG[step.channel], color: CHANNEL_FG[step.channel] }}
                          >
                            {CHANNEL_EMOJI[step.channel]} {CHANNEL_LABEL[step.channel]}
                          </span>
                          <span className="font-display text-[11px] text-[#6B7280] font-bold">
                            Día {step.dayOffset} · {String(step.hour).padStart(2, "0")}:00
                          </span>
                          {step.onlyHot && (
                            <span className="font-display text-[9.5px] font-bold tracking-wider text-[#92400E] bg-[#FEF3C7] px-1.5 py-0.5 rounded uppercase">
                              Solo calientes
                            </span>
                          )}
                        </div>
                        <div className="text-[13.5px] font-bold text-[#111]">
                          {tpl ? tpl.name : <span className="text-[#9CA3AF] italic font-normal">Sin plantilla asignada</span>}
                        </div>
                        {tpl && (
                          <div className="text-[11.5px] text-[#6B7280] mt-1 line-clamp-2 font-display">
                            {tpl.subject ?? tpl.body.slice(0, 100)}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={(e) => { e.stopPropagation(); removeStep(idx); }}
                        className="text-[#9CA3AF] hover:text-[#EF4444] p-1"
                        title="Eliminar paso"
                      >
                        <X size={14} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT: STEP INSPECTOR */}
        <aside className="sticky top-6 h-fit">
          {!selectedStep ? (
            <div className="bg-white border border-[#E5E7EB] rounded-[14px] p-8 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <Settings size={28} className="text-[#9CA3AF] mx-auto mb-3" strokeWidth={1.5} />
              <h3 className="font-sans font-bold text-[14px] text-[#111] mb-1">Sin paso seleccionado</h3>
              <p className="text-[12px] text-[#6B7280] font-display">
                Click en un paso para editarlo, o "Agregar paso" para crear uno nuevo.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <Card>
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#E5E7EB]">
                  <div>
                    <div className="font-display text-[10.5px] tracking-[0.16em] uppercase text-[#6B7280] font-bold">
                      Editando paso {selectedStepIdx! + 1}
                    </div>
                  </div>
                </div>

                <Label>Canal</Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {(["EMAIL", "WHATSAPP", "SMS", "VOICE_AI"] as const).map((c) => (
                    <button
                      key={c}
                      onClick={() => updateStep(selectedStepIdx!, { channel: c, messageTemplateId: null })}
                      className={`px-2.5 py-1.5 rounded-[8px] font-display font-bold text-[11px] tracking-wide ${
                        selectedStep.channel === c
                          ? ""
                          : "border-[1.5px] border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#9CA3AF]"
                      }`}
                      style={
                        selectedStep.channel === c
                          ? { background: CHANNEL_BG[c], color: CHANNEL_FG[c], borderColor: CHANNEL_FG[c], borderWidth: 1.5, borderStyle: "solid" }
                          : undefined
                      }
                    >
                      {CHANNEL_EMOJI[c]} {CHANNEL_LABEL[c]}
                    </button>
                  ))}
                </div>

                <Label className="mt-4">Plantilla</Label>
                <select
                  value={selectedStep.messageTemplateId ?? ""}
                  onChange={(e) => updateStep(selectedStepIdx!, { messageTemplateId: e.target.value || null })}
                  className="w-full px-3 py-2 bg-white border-[1.5px] border-[#E5E7EB] rounded-[8px] text-[12.5px] font-sans focus:border-[#CD0985] focus:outline-none"
                >
                  <option value="">— Sin plantilla —</option>
                  {templatesByChannel(selectedStep.channel).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {templatesByChannel(selectedStep.channel).length === 0 && (
                  <p className="text-[11px] text-[#92400E] mt-1.5 font-display">
                    No hay plantillas {CHANNEL_LABEL[selectedStep.channel]}.{" "}
                    <Link href="/plantillas/nueva" className="underline">Crear una →</Link>
                  </p>
                )}

                <Label className="mt-4">Día (offset desde import)</Label>
                <input
                  type="number"
                  min={0}
                  max={60}
                  value={selectedStep.dayOffset}
                  onChange={(e) => updateStep(selectedStepIdx!, { dayOffset: Number(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-white border-[1.5px] border-[#E5E7EB] rounded-[8px] text-[12.5px] font-sans focus:border-[#CD0985] focus:outline-none"
                />

                <Label className="mt-4">Hora de envío (0-23, hora local del lead)</Label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={selectedStep.hour}
                  onChange={(e) => updateStep(selectedStepIdx!, { hour: Number(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-white border-[1.5px] border-[#E5E7EB] rounded-[8px] text-[12.5px] font-sans focus:border-[#CD0985] focus:outline-none"
                />

                <div className="mt-4 space-y-2">
                  <Toggle
                    on={selectedStep.onlyHot}
                    onToggle={() => updateStep(selectedStepIdx!, { onlyHot: !selectedStep.onlyHot })}
                    title="Solo a leads calientes"
                    subtitle="Score ≥ 70"
                  />
                  <Toggle
                    on={selectedStep.skipIfReplied}
                    onToggle={() => updateStep(selectedStepIdx!, { skipIfReplied: !selectedStep.skipIfReplied })}
                    title="Saltar si ya respondió"
                    subtitle="No mandar si hay respuesta previa"
                  />
                </div>
              </Card>

              {selectedTpl && (
                <Card>
                  <div className="flex items-center gap-2 mb-3">
                    <Eye size={14} className="text-[#6B7280]" strokeWidth={2} />
                    <span className="font-display text-[10.5px] tracking-[0.16em] uppercase text-[#6B7280] font-bold">
                      Preview con datos reales
                    </span>
                  </div>
                  {selectedTpl.subject && (
                    <>
                      <div className="font-display text-[10.5px] text-[#6B7280] mb-1 font-bold uppercase tracking-wide">
                        Asunto
                      </div>
                      <div className="text-[12.5px] font-bold text-[#111] mb-3">
                        {renderTemplate(selectedTpl.subject, sample)}
                      </div>
                    </>
                  )}
                  <div className="font-display text-[10.5px] text-[#6B7280] mb-1 font-bold uppercase tracking-wide">
                    Mensaje
                  </div>
                  <div className="text-[12px] text-[#374151] whitespace-pre-wrap leading-relaxed bg-[#F9FAFB] border border-[#E5E7EB] rounded-[8px] p-3 max-h-[300px] overflow-y-auto">
                    {renderTemplate(selectedTpl.body, sample)}
                  </div>
                  <Link
                    href={`/plantillas/${selectedTpl.id}`}
                    className="block mt-3 text-center font-display text-[11px] font-bold text-[#CD0985] hover:text-[#9F0668]"
                  >
                    Editar plantilla →
                  </Link>
                </Card>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[14px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      {children}
    </div>
  );
}
function Label({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`font-display text-[10.5px] tracking-[0.16em] uppercase text-[#6B7280] font-bold mb-2 ${className}`}>
      {children}
    </div>
  );
}
function Toggle({ on, onToggle, title, subtitle }: { on: boolean; onToggle: () => void; title: string; subtitle: string }) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-2.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-[9px] text-left hover:bg-[#F3F4F6]"
    >
      <div>
        <div className="text-[12px] font-bold text-[#111]">{title}</div>
        <div className="text-[10.5px] text-[#6B7280] font-display">{subtitle}</div>
      </div>
      <div className={`w-9 h-5 rounded-full relative transition-colors ${on ? "bg-[#CD0985]" : "bg-[#E5E7EB]"}`}>
        <div
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
            on ? "left-[18px]" : "left-0.5"
          }`}
        />
      </div>
    </button>
  );
}
